import React from 'react';
import { Box, Flex, Text, Tooltip, SimpleGrid } from '@chakra-ui/react';

/**
 * Grid de checks "Materia 1..N", alineado 1:1 con el mockup ModalSimple:
 *  - Header: título en gris + contador de ordinales seleccionados en morado (derecha).
 *  - Cada item = checkbox cuadrado visible + label "Materia N".
 *  - Deshabilitados (peer o BD): fondo gris, checkbox gris relleno, texto tachado.
 *  - Seleccionados: checkbox morado con check ✓, borde morado, fondo lavanda.
 *  - Disponibles: checkbox blanco vacío, texto normal.
 */
export default function OrdinalsPicker({
  subjectsTotal,
  selected,
  reservedByPeers, // Map<ordinal, peerPaymentId>
  blockedInDb, // Map<ordinal, dbPaymentId>
  onToggle,
  disabled,
}) {
  if (!subjectsTotal || subjectsTotal <= 0) {
    return (
      <Text fontSize="xs" color="orange.700">
        Este programa no tiene materias registradas.
      </Text>
    );
  }

  const items = Array.from({ length: subjectsTotal }, (_, i) => i + 1);
  const selectedSet = new Set(selected);
  const selectedList = [...selected].sort((a, b) => a - b);

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      bg="#FAFAFC"
      borderRadius="6px"
      p={3}
    >
      <Flex align="baseline" justify="space-between" mb={2.5}>
        <Text fontSize="10px" fontWeight="700" color="gray.500" letterSpacing="0.05em">
          ¿CUÁNTAS MATERIAS APORTA ESTE PAGO?
        </Text>
        {selectedList.length > 0 ? (
          <Text fontSize="11px" color="#574feb" fontWeight="500">
            Cantidades <Text as="span" fontWeight="700">{selectedList.join(', ')}</Text> seleccionada{selectedList.length === 1 ? '' : 's'}
          </Text>
        ) : (
          <Text fontSize="11px" color="gray.400">
            Selecciona las cantidades que cubre
          </Text>
        )}
      </Flex>

      <SimpleGrid minChildWidth="104px" spacing={1.5}>
        {items.map((n) => {
          const isSelected = selectedSet.has(n);
          const peerId = reservedByPeers?.get(n);
          const dbEntry = blockedInDb?.get(n);
          const dbId = dbEntry && typeof dbEntry === 'object' ? dbEntry.paymentId : dbEntry;
          const dbSource = dbEntry && typeof dbEntry === 'object' ? dbEntry.source : 'payment';
          const isManual = dbSource === 'manual';
          const isBlocked = !isSelected && (peerId != null || dbEntry != null);
          const tooltip = peerId
            ? `Reservada por el Pago #${peerId} en este modal`
            : isManual
              ? 'Ajuste manual de cartera'
              : dbId
                ? `Ya tomada por el Pago #${dbId}`
                : '';

          const itemBg = isSelected
            ? '#E8E4FC'
            : isBlocked
              ? (isManual ? '#FEF3C7' : '#EDF2F7')
              : '#FFFFFF';
          const itemBorder = isSelected
            ? '#574feb'
            : isBlocked
              ? (isManual ? '#F59E0B' : '#EDF2F7')
              : '#E2E8F0';
          const labelColor = isSelected
            ? '#3C3799'
            : isBlocked
              ? (isManual ? '#B45309' : '#A0AEC0')
              : '#4A5568';
          const labelWeight = isSelected ? 700 : isBlocked ? 500 : 500;
          const labelDeco = isBlocked && !isManual ? 'line-through' : 'none';
          const checkBg = isSelected
            ? '#574feb'
            : isBlocked
              ? (isManual ? '#F59E0B' : '#A0AEC0')
              : 'transparent';
          const checkBorder = isSelected
            ? '#574feb'
            : isBlocked
              ? (isManual ? '#F59E0B' : '#A0AEC0')
              : '#CBD5E0';

          const item = (
            <Flex
              as="button"
              type="button"
              disabled={disabled || isBlocked}
              onClick={() => !isBlocked && !disabled && onToggle(n)}
              align="center"
              gap={2}
              px={2.5}
              py={2}
              borderWidth="1px"
              borderColor={itemBorder}
              bg={itemBg}
              borderRadius="6px"
              cursor={isBlocked || disabled ? 'not-allowed' : 'pointer'}
              opacity={isBlocked ? 0.7 : 1}
              _hover={!isBlocked && !disabled && !isSelected ? { borderColor: '#574feb', bg: '#F5F3FF' } : {}}
              w="100%"
              transition="background 0.15s, border-color 0.15s"
            >
              <Flex
                w="14px"
                h="14px"
                borderRadius="3px"
                borderWidth="1px"
                borderColor={checkBorder}
                bg={checkBg}
                align="center"
                justify="center"
                flexShrink={0}
              >
                {isSelected && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </Flex>
              <Text
                fontSize="12px"
                color={labelColor}
                fontWeight={labelWeight}
                textDecoration={labelDeco}
                lineHeight="1.2"
                whiteSpace="nowrap"
              >
                Materia {n}
              </Text>
            </Flex>
          );

          return tooltip ? (
            <Tooltip key={n} label={tooltip} placement="top" hasArrow>
              {item}
            </Tooltip>
          ) : (
            <Box key={n}>{item}</Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
